import React from 'react';
import { ListItem, ListItemSecondaryAction, ListItemText } from '@material-ui/core';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import moment from 'moment';
import IconButton from '@material-ui/core/IconButton';
import { Close } from '@mui/icons-material';

import config from 'config';
import makeSortableItem from 'retired/SortableItem';
import {
  black,
  purple,
  statusError,
  statusPending,
  statusSuccess,
  statusTextError,
  statusTextPending,
  statusTextSuccess,
} from 'utils/color-styles';
import PublishedIcon from 'assets/icons/tick_published.svg';
import PendingIcon from 'assets/icons/pending.svg';
import DraftIcon from 'assets/icons/draft.svg';
import Img from 'retired/Img';
import { renderAddress } from 'utils/renderAddress';

interface ContentListItemProps {
  content: any;
  onRemove: () => void;
  sortable?: boolean;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'draft':
      return DraftIcon;

    case 'pending':
      return PendingIcon;

    case 'published':
      return PublishedIcon;

    default:
      return null;
  }
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case 'draft':
      return {
        background: statusError,
        color: statusTextError,
      };

    case 'pending':
      return {
        background: statusPending,
        color: statusTextPending,
      };

    case 'published':
      return {
        background: statusSuccess,
        color: statusTextSuccess,
      };
    default:
      return {};
  }
};

const ContentListItem: React.FC<ContentListItemProps> = ({
  content,
  onRemove,
  sortable = true,
}) => {
  const rawContent = content?.toJS ? content.toJS() : content;
  const location = renderAddress(rawContent.locationObject);
  const statusIcon = getStatusIcon(rawContent.status);
  const type = rawContent.__t || rawContent.type || '';

  return (
    <div
      style={{
        width: '100%',
        cursor: sortable ? 'move' : 'default',
        position: 'relative',
        pointerEvents: 'none',
      }}
    >
      {sortable && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            textAlign: 'center',
            top: 8,
          }}
        >
          <DragHandleIcon />
        </div>
      )}

      <ListItem
        key={rawContent.id}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.03)',
          borderRadius: 16,
          marginBottom: 8,
        }}
      >
        <ListItemText
          primary={
            <div style={{ display: 'flex', paddingTop: sortable ? 20 : 0 }}>
              <div style={{ color: purple }}>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  href={`${config.organizerUri}/deeds/${type.toLowerCase()}s/${rawContent.id}`}
                  target="_blank"
                  style={{ cursor: 'pointer', pointerEvents: 'initial' }}
                >
                  {rawContent.name}
                </a>
                {typeof rawContent.virtual === 'boolean' && (
                  <span style={{ color: black }}>
                    &nbsp;- {rawContent.virtual ? 'Virtual' : 'In Person'}
                  </span>
                )}
              </div>
            </div>
          }
          secondary={
            <>
              {rawContent.startingAt ? (
                <div>{moment(rawContent.startingAt).format('LLLL')}</div>
              ) : null}
              <div>{location}</div>
            </>
          }
        />

        {rawContent.status && (
          <div
            style={{
              fontSize: 12,
              width: 120,
              textAlign: 'center',
              padding: 8,
              borderRadius: 20,
              textTransform: 'capitalize',
              ...getStatusStyle(rawContent.status),
            }}
          >
            {rawContent.status}&nbsp;&nbsp;
            {statusIcon && <Img src={statusIcon} alt="status" />}
          </div>
        )}

        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            aria-label="delete"
            onClick={onRemove}
            style={{ pointerEvents: 'initial' }}
          >
            <Close />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    </div>
  );
};

const SortableContentListItem = makeSortableItem({
  itemType: 'content',
  wrapperStyle: {
    position: 'relative',
    maxWidth: 1000,
    paddingTop: 6,
    paddingBottom: 2,
    padding: 0,
    marginBottom: 10,
  },
})(ContentListItem);

export default (props: ContentListItemProps) => {
  if (props.sortable === false) {
    return <ContentListItem {...props} />;
  }
  return <SortableContentListItem {...props} />;
};
